// Helpers for the weekly stream schedule (public/weeklySchedule.json).
//
// The file holds dated week blocks (Monday starts). Stream times are authored
// as wall-clock times in the schedule's IANA timezone (schedule.timezone) and
// converted to real instants per calendar date, so daylight-saving shifts are
// handled correctly year-round.

export const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// "7:00am - 9:00am"
export const TIME_RANGE_PATTERN = /^(\d{1,2}):(\d{2})(am|pm)\s*-\s*(\d{1,2}):(\d{2})(am|pm)$/i;

// Monday date of a week block: "2026-07-20"
export const WEEK_START_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function parseTimeRange(timeString) {
  const match = TIME_RANGE_PATTERN.exec(timeString || '');
  if (!match) return null;

  const to24Hour = (hour, period) => {
    let h = parseInt(hour, 10);
    if (period.toLowerCase() === 'pm' && h !== 12) h += 12;
    if (period.toLowerCase() === 'am' && h === 12) h = 0;
    return h;
  };

  return {
    start: { hour: to24Hour(match[1], match[3]), minute: parseInt(match[2], 10) },
    end: { hour: to24Hour(match[4], match[6]), minute: parseInt(match[5], 10) },
  };
}

// Offset (ms) of `timeZone` from UTC at the given timestamp.
function timeZoneOffsetMs(timeZone, timestamp) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = {};
  for (const { type, value } of formatter.formatToParts(new Date(timestamp))) {
    parts[type] = value;
  }
  const asUTC = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour, 10) % 24,
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  return asUTC - timestamp;
}

// Convert a wall-clock time in `timeZone` to a real Date. `month` is 1-based.
export function zonedTimeToDate(timeZone, year, month, day, hour, minute) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  let timestamp = utcGuess;
  // Two passes converge across DST transitions.
  for (let i = 0; i < 2; i++) {
    timestamp = utcGuess - timeZoneOffsetMs(timeZone, timestamp);
  }
  return new Date(timestamp);
}

export function toISODateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Monday (ISO week) of the local calendar week containing `date`, as YYYY-MM-DD.
export function mondayISOOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const mondayOffset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - mondayOffset);
  return toISODateLocal(d);
}

// Sorted copy of schedule.weeks (ascending by start). Empty if missing.
export function sortedWeekBlocks(schedule) {
  const weeks = Array.isArray(schedule?.weeks) ? [...schedule.weeks] : [];
  return weeks.sort((a, b) => String(a.start).localeCompare(String(b.start)));
}

// Pick the week block that owns `date`. Uses the latest block whose Monday
// start is on or before this date's Monday. Dates after the last authored
// week keep using that last block until a newer one is published.
export function weekBlockForDate(schedule, date) {
  const monday = mondayISOOf(date);
  const weeks = sortedWeekBlocks(schedule);
  if (weeks.length === 0) return null;

  let chosen = null;
  for (const week of weeks) {
    if (week.start <= monday) chosen = week;
    else break;
  }
  return chosen || weeks[0];
}

// Day-slot map for `date` (monday…sunday arrays), or null if none.
export function daysForDate(schedule, date) {
  const block = weekBlockForDate(schedule, date);
  return block?.days || null;
}

// How many weeks `date` is from `fromDate`'s week (-1 previous, 0 same, 1 next…).
export function weekOffsetFrom(fromDate, date) {
  const from = new Date(fromDate);
  from.setHours(0, 0, 0, 0);
  const fromMondayOffset = (from.getDay() + 6) % 7;
  from.setDate(from.getDate() - fromMondayOffset);

  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const targetMondayOffset = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - targetMondayOffset);

  return Math.round((target - from) / (7 * 24 * 60 * 60 * 1000));
}

// Streams scheduled on the weekday of `date`, each with concrete
// `startTime`/`endTime` Date instants for that calendar date.
export function streamInstancesForDate(schedule, date) {
  const dayKey = DAY_KEYS[(date.getDay() + 6) % 7];
  const days = daysForDate(schedule, date);
  const streams = (days && days[dayKey]) || [];

  return streams
    .map((stream) => {
      const range = parseTimeRange(stream.time);
      if (!range) return null;
      const startTime = zonedTimeToDate(
        schedule.timezone,
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        range.start.hour,
        range.start.minute
      );
      const endTime = zonedTimeToDate(
        schedule.timezone,
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        range.end.hour,
        range.end.minute
      );
      return { ...stream, dayKey, date, startTime, endTime };
    })
    .filter(Boolean)
    .sort((a, b) => a.startTime - b.startTime);
}

// The next stream (by start time) whose end is still in the future,
// searching from today up to `horizonDays` ahead.
export function findNextStream(schedule, now = new Date(), horizonDays = 14) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  for (let offset = 0; offset <= horizonDays; offset++) {
    const date = new Date(today);
    date.setDate(today.getDate() + offset);
    const upcoming = streamInstancesForDate(schedule, date).filter(
      (instance) => instance.endTime > now
    );
    if (upcoming.length > 0) return upcoming[0];
  }
  return null;
}
